// Centralised grant write helper. Every grant write in the intranet
// goes through here so governance (legacy, source of truth today) and
// financials (new home, read target under USE_FINANCIALS_PROJECT) stay
// in sync during the cutover window.
//
// Flip `DUAL_WRITE_FINANCIALS` true once intranet reads from financials
// are validated. Writes keep landing in governance first — financials
// mirror is a best-effort side effect. If the mirror fails we log and
// continue; governance remains authoritative until the teardown phase.
//
// Identity map: financials.grants rows migrated from governance carry
// `source='user_grants'` + `source_id=<governance.user_grants.id>`. The
// unique index `(source, source_id) where source_id is not null` makes
// upserts idempotent, so an accidental double-write is harmless.
//
// migrated 2026-04-27 to SDK route workspaceProject.userGrants /
// workspaceProject.people (governance writes flow through the workspace
// REST wrapper; financials mirror keeps using grantsClient because the
// financials project is on a separate Supabase tenant managed by
// @symbo.ls/finance-features).

import { getSDK } from '../../shared/functions/sdk.js'
import { grants } from '@symbo.ls/finance-features'
import { DUAL_WRITE_FINANCIALS } from './featureFlags.js'

const SYMBOLS_ORG_ID = '69b77a61e0e0de1aa47d01cb'

// Map governance user_grants columns → financials.grants columns.
// Identity; governance.user_grants and financials.grants share the
// same conceptual shape by design. Keep here to isolate any future
// schema drift.
const _toFinancials = (row, granteeEmail) => ({
  org_id: SYMBOLS_ORG_ID,
  grantee_email: granteeEmail,
  share_class: row.share_class ?? null,
  shares_manual: row.shares_manual ?? null,
  shares: row.shares ?? null,
  equity_pct: row.equity_pct ?? null,
  vested_shares: row.vested_shares ?? null,
  vesting_start: row.vesting_start ?? null,
  vesting_end: row.vesting_end ?? null,
  vesting_months: row.vesting_months ?? null,
  cliff_months: row.cliff_months ?? null,
  salary_monthly: row.salary_monthly ?? null,
  hourly_rate: row.hourly_rate ?? null,
  weekly_hours: row.weekly_hours != null ? String(row.weekly_hours) : null,
  contract_length: row.contract_length ?? null,
  investment: row.investment ?? null,
  investment_type: row.investment_type ?? null,
  origin_valuation: row.origin_valuation ?? null,
  discount: row.discount ?? null,
  status: row.status ?? 'active',
  label: row.label ?? null,
  notes: row.notes ?? null,
  source: 'user_grants',
  source_id: String(row.id),
  updated_at: new Date().toISOString()
})

// Resolve the grantee email for a given user_id. user_grants is keyed
// by user_id; financials.grants is keyed by grantee_email. Looked up
// via the SDK's workspaceProject.people endpoint (server-side join over
// user_profiles + auth.users — same canonical source as the legacy
// public.people view).
const _emailFor = async (userId) => {
  try {
    const list = await getSDK().execute('workspaceProject.people', 'list',
      { filter: { user_id: userId }, limit: 1, single: true })
    if (Array.isArray(list)) return list[0]?.email || null
    return list?.email || null
  } catch (e) {
    console.warn('[grantsWriter] people lookup failed', e?.message || e)
    return null
  }
}

// Mirror a governance upsert/update into financials. Best effort —
// failures warn but do not throw.
const _mirror = async (row, granteeEmail) => {
  if (!DUAL_WRITE_FINANCIALS) return
  if (!granteeEmail) {
    console.warn('[grantsWriter] no grantee email; skipping financials mirror', row?.id)
    return
  }
  const fin = _toFinancials(row, granteeEmail)
  const client = grants.grantsClient()
  if (!client) return
  const { error } = await client.from('grants').upsert(fin, { onConflict: 'source,source_id' })
  if (error) console.warn('[grantsWriter] financials mirror failed', error.message)
}

// Mirror a delete as a soft-void on financials. Hard deletes violate
// audit chain-of-custody; the financials schema keeps the row with
// status='void' so cap-table history stays auditable.
const _mirrorDelete = async (governanceId) => {
  if (!DUAL_WRITE_FINANCIALS) return
  const client = grants.grantsClient()
  if (!client) return
  const { error } = await client
    .from('grants')
    .update({ status: 'void', updated_at: new Date().toISOString() })
    .eq('source', 'user_grants')
    .eq('source_id', String(governanceId))
  if (error) console.warn('[grantsWriter] financials void-mirror failed', error.message)
}

// Public API — every grant write site in the intranet calls these
// instead of hitting `user_grants` directly. Each call routes through
// the SDK; the `{ data, error }` envelope is preserved at the public
// boundary so existing consumers (CapTableGrantModal, drilldown forms)
// keep working unchanged.

export const updateGrant = async (grantId, patch) => {
  try {
    const data = await getSDK().execute('workspaceProject.userGrants', 'update',
      { id: grantId, payload: patch })
    if (!data) return { data: null, error: new Error('grant update returned empty') }
    const email = await _emailFor(data.user_id)
    await _mirror(data, email)
    return { data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const insertGrant = async (row) => {
  try {
    const data = await getSDK().execute('workspaceProject.userGrants', 'create',
      { payload: row })
    if (!data) return { data: null, error: new Error('grant insert returned empty') }
    const email = await _emailFor(data.user_id)
    await _mirror(data, email)
    return { data, error: null }
  } catch (error) {
    return { data: null, error }
  }
}

export const deleteGrant = async (grantId) => {
  // Capture grantee info before the governance row disappears so the
  // financials void-mirror can keep the row attributable.
  await getSDK().execute('workspaceProject.userGrants', 'remove', { id: grantId })
  await _mirrorDelete(grantId)
}
